import { useQuery } from '@apollo/client/react';
import {
  type CompleteServiceExecutionFragment,
  GetServiceExecutionsDocument,
  ServiceState,
} from '@drycut/graphql';
import { Heading, Stack } from '@drycut/ui-components';

import { PageLoading } from '../PageLoading';
import { StylistManualBooking } from '../SylistManualBooking';
import OnHoldServicesTable from './OnHoldServices/OnHoldServicesTable';
import { PreBookingsTable } from './PreBookingsTable';
import QueuedServicesTable from './QueuedServices/QueuedServicesTable';

export function Queues(): React.ReactElement {
  // Get the the data from the server the first time and then use the cache if present.
  const { data, loading } = useQuery(GetServiceExecutionsDocument, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const serviceExecutions = data?.serviceExecutions?.nodes || [];

  const filteredExecutions = serviceExecutions.filter(
    (service): service is CompleteServiceExecutionFragment => Boolean(service),
  );

  const sortByOrder = (executions: CompleteServiceExecutionFragment[]) =>
    executions.sort((a, b) => (a.order || 0) - (b.order || 0));

  const startedServices = filteredExecutions.filter(
    (service) => service.state === ServiceState.Started,
  );

  const queuedServices = filteredExecutions.filter(
    (service) => service.state === ServiceState.Queued || service.state === ServiceState.Called,
  );

  const onHoldServices = filteredExecutions.filter(
    (service) => service.state === ServiceState.OnHold,
  );

  if (loading) return <PageLoading />;

  return (
    <div className="grid grid-cols-12 gap-4 h-full">
      <Stack className="border p-3 relative col-span-8" gap={8}>
        <Stack gap={3}>
          <Heading variant="h2" className="text-2xl">
            Warteschlange
          </Heading>

          <StylistManualBooking className="absolute top-5 right-5 w-fit" />

          <QueuedServicesTable
            services={[...sortByOrder(startedServices), ...sortByOrder(queuedServices)]}
          />
        </Stack>

        <Stack gap={3}>
          <Heading variant="h2" className="text-2xl">
            Pre Bookings für heute
          </Heading>

          <PreBookingsTable />
        </Stack>
      </Stack>

      <div className="p-3 bg-zinc-100 col-span-4">
        <Heading variant="h2" className="text-2xl">
          No Shows
        </Heading>

        <div className="pt-5">
          <OnHoldServicesTable services={sortByOrder(onHoldServices)} />
        </div>
      </div>
    </div>
  );
}
